import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Divider,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Autocomplete,
  Chip,
  IconButton,
  ListSubheader,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import ShareIcon from '@mui/icons-material/Share';
import LocalHospital from '@mui/icons-material/LocalHospital';

import theme from '../../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, loadDraft, clearDraft } from '../../utils/draftUtils';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../lib/constants';
const OperationSheet = () => {
  const { showNotification } = useNotification();
  const signaturePadRef = useRef(null);
  const [success, setSuccess] = useState("");
  const { uhid_no } = useParams(); // Get the UHID number from the URL parameters
  const navigate = useNavigate();
  const [error, setError] = useState(null); // Add this line
  const [loading, setLoading] = useState(false); // Add loading state
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case 'pre_op_diagnosis':
        if (!value) error = "Pre Op Diagnosis is required";
        break;
      case 'type_of_procedure':
        if (!value) error = "Type of procedure is required";
        break;
      case 'post_op_diagnosis':
        if (!value) error = "Post Op Diagnosis is required";
        break;
      case 'name_of_procedure':
        if (!value) error = "Name of Procedure is required";
        break;
      case 'operation_date':
        if (!value) error = "Operation Date is required";
        break;
      case 'operation_start_time':
        if (!value) error = "Time is required";
        break;
      case 'duration_of_procedure':
        if (!value) error = "Duration is required";
        break;
      case 'name_of_surgeon':
        if (!value) error = "Surgeon name is required";
        break;
      case 'name_of_anesthesiologist':
        if (!value) error = "Anaesthesiologist name is required";
        break;
      case 'scrubbed_nurse':
        if (!value) error = "Scrubbed Nurse is required";
        break;
      case 'type_of_anesthesia':
        if (!value) error = "Type of anesthesia is required";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const [formData, setFormData] = useState({
    uhid_no: uhid_no || "",
    pre_op_diagnosis: '',
    type_of_procedure: '',
    post_op_diagnosis: '',
    name_of_procedure: '',
    operation_date: '',
    operation_start_time: '',
    duration_of_procedure: '',
    name_of_surgeon: '',
    assisted_by: '',
    name_of_anesthesiologist: '',
    scrubbed_nurse: '',
    type_of_anesthesia: '',
    circulating_nurse: '',
    operation_notes: '',
    findings: ''
  });

  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSigner, setCurrentSigner] = useState("");

  const validate = () => {
    const tempErrors = {};
    if (!formData.pre_op_diagnosis) tempErrors.pre_op_diagnosis = "Pre Op Diagnosis is required";
    if (!formData.type_of_procedure) tempErrors.type_of_procedure = "Type of procedure is required";
    if (!formData.post_op_diagnosis) tempErrors.post_op_diagnosis = "Post Op Diagnosis is required";
    if (!formData.name_of_procedure) tempErrors.name_of_procedure = "Name of Procedure is required";
    if (!formData.operation_date) tempErrors.operation_date = "Operation Date is required";
    if (!formData.operation_start_time) tempErrors.operation_start_time = "Time is required";
    if (!formData.duration_of_procedure) tempErrors.duration_of_procedure = "Duration is required";
    if (!formData.name_of_surgeon) tempErrors.name_of_surgeon = "Surgeon name is required";
    if (!formData.name_of_anesthesiologist) tempErrors.name_of_anesthesiologist = "Anaesthesiologist name is required";
    if (!formData.scrubbed_nurse) tempErrors.scrubbed_nurse = "Scrubbed Nurse is required";
    if (!formData.type_of_anesthesia) tempErrors.type_of_anesthesia = "Type of anesthesia is required";

    setErrors(tempErrors);

    if (Object.keys(tempErrors).length > 0) {
      const firstErrorField = Object.keys(tempErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      showNotification('error', "Please fill in all required fields.");
    }

    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError(null);

    try {
      if (success === "success") {
        const responseUpdate = await axios.put(`${API_BASE_URL}/update_operation_sheet/${uhid_no}`, formData);
        showNotification('success', 'Operation Sheet form updated successfully!');
      } else {
        const response = await axios.post(`${API_BASE_URL}/operation_sheet`, formData);
        showNotification('success', 'Operation Sheet form submitted successfully!');
        console.log(response.data.message);
      }
      // Clear draft after successful submit
      clearDraft(DRAFT_KEYS.OPERATION_SHEET, uhid_no);

      navigate(`/mainPage/${formData.uhid_no}`, {
        state: { from: 'IntraOperativePhase', status: 'success', step: 2 },
      });
    } catch (error) {
      console.error("There was an error submitting the form:", error);
      setError("Failed to submit operation sheet. Please try again.");
      showNotification('error', "Error submitting operation sheet. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureOpen = (signer) => {
    setCurrentSigner(signer);
    setSignatureDialogOpen(true);
  };

  const handleSignatureClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handleSignatureSave = () => {
    if (signaturePadRef.current) {
      const signatureData = signaturePadRef.current.toDataURL();
      setFormData((prevData) => ({
        ...prevData,
        signature: {
          ...prevData.signature,
          [currentSigner]: signatureData,
        },
      }));
      setSignatureDialogOpen(false);
    }
  };

  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhidNo}`);
      console.log(response.data);
      setFormData(prev => ({
        ...prev,
        name: response.data.name,
        age: response.data.age,
        sex: response.data.sex,
      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setError('Failed to fetch patient data. Please try again.');
      setLoading(false);
    }
  };
  // Add this helper function above your useEffect hook
  const getDurationLabel = (minutes) => {
    if (minutes === 15) return "15 minutes";
    if (minutes === 30) return "30 minutes";
    if (minutes === 45) return "45 minutes";
    if (minutes === 60) return "1 hour";
    if (minutes === 90) return "1.5 hours";
    if (minutes === 120) return "2 hours";
    if (minutes === 150) return "2.5 hours";
    if (minutes === 180) return "3 hours";
    if (minutes > 180) return "More than 3 hours";
    return "";
  };

  useEffect(() => {
    // Load draft if exists
    try {
      const draft = loadDraft(DRAFT_KEYS.OPERATION_SHEET, uhid_no);
      if (draft && draft.formData) {
        setFormData(prev => ({ ...prev, ...draft.formData }));
      }
    } catch (e) {
      console.error("Error loading draft:", e);
    }

    axios.get(`${API_BASE_URL}/get_operation_sheet/${uhid_no}`)
      .then((response) => {
        console.log('Fetched data:', response.data);

        const dateFields = ["operation_start_time"];
        const date = ["operation_date"];

        // Format datetime fields (e.g., "2025-10-14T13:00")
        const formattedDate = dateFields.reduce((acc, field) => {
          if (response.data[field]) {
            acc[field] = new Date(response.data[field]).toISOString().slice(0, 16);
          }
          return acc;
        }, {});

        // Format date-only fields (e.g., "2025-10-14")
        const formattedData = date.reduce((acc, field) => {
          if (response.data[field]) {
            acc[field] = new Date(response.data[field]).toISOString().slice(0, 10);
          }
          return acc;
        }, {});

        // Map backend duration (like "30") to frontend label (like "30 minutes")
        const durationMap = {
          "15": "15 minutes",
          "30": "30 minutes",
          "45": "45 minutes",
          "60": "1 hour",
          "90": "1.5 hours",
          "120": "2 hours",
          "150": "2.5 hours",
          "180": "3 hours",
          "999": "More than 3 hours"  // Adjust if backend uses a different value
        };

        const backendDuration = response.data.duration_of_procedure;
        const mappedDuration = durationMap[backendDuration] || '';

        setFormData({
          ...response.data,
          ...formattedData,
          ...formattedDate,
          duration_of_procedure: mappedDuration,
        });

        setSuccess("success");
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }, [uhid_no]);


  const diagnosisOptions = [
    "Appendicitis",
    "Cholecystitis",
    "Hernia",
    "Fracture",
    "Pneumonia",
    "Diabetes",
    "Hypertension",
    "Asthma"
  ];

  const postOpDiagnosisOptions = [
    "Appendectomy",
    "Cholecystectomy",
    "Hernia Repair",
    "Fracture Fixation",
    "CABG",
    "Cataract Removal",
    "Hysterectomy",
    "Laparotomy"
  ];

  const procedureOptions = [
    "Appendectomy",
    "Cataract Surgery",
    "Hernia Repair",
    "Cholecystectomy",
    "Laparoscopy",
    "C-section",
    "Knee Replacement",
    "Hip Replacement",
    "CABG",
    "Endoscopy"
  ];

  const durationOptions = [
    "15 minutes",
    "30 minutes",
    "45 minutes",
    "1 hour",
    "1.5 hours",
    "2 hours",
    "2.5 hours",
    "3 hours",
    "More than 3 hours"
  ];

  const operationNoteSuggestions = [
    "Procedure completed without complications.",
    "Patient tolerated the procedure well.",
    "Hemostasis secured.",
    "No adverse events noted during surgery.",
  ];

  const doctorGroups = {
    Cardiologists: [
      "Dr. John Smith",
      "Dr. Emily Johnson",
      "Dr. Michael Brown",
      "Dr. Sarah Davis",
      "Dr. William Wilson",
      "Dr. Linda Martinez"
    ],
    Neurologists: [
      "Dr. Robert Lee",
      "Dr. Patricia Taylor",
      "Dr. James Anderson",
      "Dr. Barbara Thomas",
      "Dr. David Jackson",
      "Dr. Susan White"
    ],
    Orthopedics: [
      "Dr. Richard Garcia",
      "Dr. Jennifer Harris",
      "Dr. Charles Clark",
      "Dr. Jessica Lewis",
      "Dr. Thomas Robinson",
      "Dr. Karen Walker"
    ],
    Dermatologists: [
      "Dr. Nancy Young",
      "Dr. Matthew Hall",
      "Dr. Donna Allen",
      "Dr. Steven King",
      "Dr. Michelle Wright",
      "Dr. George Scott"
    ],
    Gynecologists: [
      "Dr. Sandra Green",
      "Dr. Kevin Adams",
      "Dr. Laura Baker",
      "Dr. Mark Nelson",
      "Dr. Angela Carter",
      "Dr. Brian Mitchell"
    ],
    Pediatricians: [
      "Dr. Melissa Perez",
      "Dr. Eric Roberts",
      "Dr. Deborah Turner",
      "Dr. Jason Phillips",
      "Dr. Rebecca Campbell",
      "Dr. Andrew Parker"
    ],
    AnaesthesiaSpecialists: [
      "Dr. Anil Mehta",
      "Dr. Priya Iyer",
      "Dr. Rakesh Nair",
      "Dr. Neha Sharma",
      "Dr. Vivek Rao",
      "Dr. Aarti Desai"
    ]
  };
  const [savedData, setSavedData] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [currentPage, setCurrentPage] = useState('form');
  const [dataLoaded, setDataLoaded] = useState(false);
  // Auto-save functionality
  // Auto-save functionality - only after initial data is loaded
  useEffect(() => {
    if (!dataLoaded) return; // Don't auto-save until initial data is loaded

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(formData);
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 2000);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded]);

  // Save form data to sessionStorage
  const saveFormData = (data) => {
    sessionStorage.setItem(`operation_sheetForm_${uhid_no}`, JSON.stringify(data));
    console.log('Form data saved:', data);
  };

  // Get saved form data
  const getSavedFormData = () => {
    const saved = sessionStorage.getItem(`operation_sheetForm_${uhid_no}`);
    return saved ? JSON.parse(saved) : null;
  };
  useEffect(() => {
    const saved = getSavedFormData();
    if (saved && Object.keys(saved).length > 0) {
      setSavedData(saved);
      setFormData(saved);
    }
  }, []);
  // Check if form has any data
  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(subValue =>
          typeof subValue === 'string' ? subValue.trim() !== '' : subValue
        );
      }
      return value === true;
    });
  };

  // Clear saved data - ONLY called on successful submit
  const clearSavedData = () => {
    setSavedData(null);
    sessionStorage.removeItem(`operation_sheetForm_${uhid_no}`);
    sessionStorage.removeItem(`formStatus_operation_sheet_${uhid_no}`);
  };

  // Home button handler
  const handleHomeNavigation = () => {
    if (hasFormData()) {
      saveFormData(formData);
      // Store form status for home page to show "Continue" button
      sessionStorage.setItem(`formStatus_operation_sheet_${uhid_no}`, JSON.stringify({
        formName: 'OPERATION SHEET',
        uhid_no: uhid_no,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/operationSheet/${uhid_no}`
      }));
    }
    navigate('/home');
  };
  const handleBack = () => {
    navigate(`/mainPage/${formData.uhid_no}`, {
      state: { from: 'IntraOperativePhase', status: 'success', step: 2 },
    }); // Navigate to the previous page
  };
  const nurseGroups = {
    ICU: [
      "Nurse Anna Smith",
      "Nurse Brian Lee",
      "Nurse Carol White",
      "Nurse David Green",
      "Nurse Emma Black",
      "Nurse Frank Harris",
    ],
    Pediatrics: [
      "Nurse Emily Davis",
      "Nurse Frank Miller",
      "Nurse Grace Wilson",
      "Nurse Hannah Moore",
      "Nurse Ian Clark",
      "Nurse Julia Lewis",
    ],
    GeneralWard: [
      "Nurse Henry Brown",
      "Nurse Irene Clark",
      "Nurse Jack Harris",
      "Nurse Kelly Martinez",
      "Nurse Liam Thompson",
      "Nurse Mia Anderson",
    ],
    Emergency: [
      "Nurse Karen Scott",
      "Nurse Larry Adams",
      "Nurse Monica Turner",
      "Nurse Nathan Hill",
      "Nurse Olivia King",
      "Nurse Peter Wright",
    ],
    Surgery: [
      "Nurse Quinn Young",
      "Nurse Rachel Walker",
      "Nurse Samuel Hall",
      "Nurse Tina Allen",
      "Nurse Victor Baker",
      "Nurse Wendy Nelson",
    ],
    Maternity: [
      "Nurse Xavier Perez",
      "Nurse Yolanda Roberts",
      "Nurse Zoe Campbell",
      "Nurse Aaron Phillips",
      "Nurse Bethany Parker",
      "Nurse Chris Turner",
    ],
  };

  const findingsSuggestions = [
    "No significant abnormalities found.",
    "Tissue appeared healthy and viable.",
    "Signs of inflammation observed.",
    "No evidence of infection or necrosis.",
  ];

  const addSuggestion = (field, text) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field] ? prev[field] + " " + text : text,
    }));
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/[^a-zA-Z\s,._]/g, '');
    setFormData({ ...formData, [name]: sanitizedValue });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const goToVideoRecorder = (formName, uhid_no) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };

  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const handleShareClick = async () => {
    try {
      // Call API to generate a share token for the given UHID
      const res = await axios.post(`${API_BASE_URL}/generate_for_operationSheet_share_token/${uhid_no}`);

      const token = res.data.token;
      const link = `${API_BASE_URL}/operationSheet/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative analgesia chart:\n\n${link}`;

      // Copy the shareable link to clipboard
      await navigator.clipboard.writeText(link);

      // Show success notification to user
      setSubmitStatus({
        open: true,
        message: "Secure link copied to clipboard!",
        severity: "info",
      });

      // Open WhatsApp chat with pre-filled message (replace phone number if needed)
      const phoneNumber = '+916379605575'; // no spaces or special chars for wa.me
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);

      // Show error notification to user
      setSubmitStatus({
        open: true,
        message: "Failed to generate shareable link",
        severity: "error",
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              OPERATION SHEET
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            {/* Left-aligned Back Button */}
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ marginLeft: 2 }}
            >
              Back
            </Button>

            {/* Right-aligned Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={() => goToVideoRecorder("OPERATION SHEET", uhid_no)}
                sx={{ marginRight: 1 }}
              >
                Add Iterative Video
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>


          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={diagnosisOptions}
                value={formData.pre_op_diagnosis}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, pre_op_diagnosis: newInputValue });
                  if (errors.pre_op_diagnosis) {
                    setErrors(prev => ({ ...prev, pre_op_diagnosis: '' }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pre Op Diagnosis"
                    name="pre_op_diagnosis"
                    required
                    fullWidth
                    error={!!errors.pre_op_diagnosis}
                    helperText={errors.pre_op_diagnosis}
                    onBlur={handleBlur}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                label="Type of procedure"
                name="type_of_procedure"
                value={formData.type_of_procedure}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                fullWidth
                error={!!errors.type_of_procedure}
                helperText={errors.type_of_procedure}
              >
                <MenuItem value="Major">Major</MenuItem>
                <MenuItem value="Minor">Minor</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="UHID No"
                name="uhid_no"
                value={formData.uhid_no}
                onChange={(e) => setFormData({ ...formData, uhid_no: e.target.value })}
                required
                fullWidth
                InputProps={{ readOnly: true, }}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={postOpDiagnosisOptions}
                value={formData.post_op_diagnosis}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, post_op_diagnosis: newInputValue });
                  if (errors.post_op_diagnosis) {
                    setErrors(prev => ({ ...prev, post_op_diagnosis: '' }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Post Op Diagnosis"
                    name="post_op_diagnosis"
                    required
                    fullWidth
                    error={!!errors.post_op_diagnosis}
                    helperText={errors.post_op_diagnosis}
                    onBlur={handleBlur}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={procedureOptions}
                value={formData.name_of_procedure}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, name_of_procedure: newInputValue });
                  if (errors.name_of_procedure) {
                    setErrors(prev => ({ ...prev, name_of_procedure: '' }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Name of Procedure"
                    name="name_of_procedure"
                    required
                    fullWidth
                    error={!!errors.name_of_procedure}
                    helperText={errors.name_of_procedure}
                    onBlur={handleBlur}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Operation Date"
                name="operation_date"
                type="date"
                value={formData.operation_date}
                onChange={handleChange}
                onBlur={handleBlur}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
                error={!!errors.operation_date}
                helperText={errors.operation_date}
                inputProps={{
                  min: new Date().toISOString().split("T")[0], // optional: prevent past dates
                }}
              />

            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Time"
                name="operation_start_time"
                type="DateTime-local" inputProps={{
                  min: new Date().toISOString().slice(0, 16), // Sets the minimum date and time to the current date and time
                }}
                value={formData.operation_start_time}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                fullWidth
                error={!!errors.operation_start_time}
                helperText={errors.operation_start_time}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Duration of Procedure"
                name="duration_of_procedure"
                value={formData.duration_of_procedure}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                fullWidth
                error={!!errors.duration_of_procedure}
                helperText={errors.duration_of_procedure}
              >
                {durationOptions.map((duration, index) => (
                  <MenuItem key={index} value={duration}>
                    {duration}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.name_of_surgeon}>
                <InputLabel id="surgeon-select-label">Name of Surgeon</InputLabel>
                <Select
                  labelId="surgeon-select-label"
                  id="surgeon-select"
                  name="name_of_surgeon"
                  value={formData.name_of_surgeon || ''}
                  label="Name of Surgeon"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {Object.entries(doctorGroups).map(([group, doctors]) => [
                    <ListSubheader key={`header-${group}`}>
                      {group}
                    </ListSubheader>,
                    ...doctors.map((doctor, index) => (
                      <MenuItem key={`${group}-${doctor}-${index}`} value={doctor}>
                        {doctor}
                      </MenuItem>
                    ))
                  ]).flat()}
                </Select>
                {errors.name_of_surgeon && <FormHelperText>{errors.name_of_surgeon}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Assisted By"
                name="assisted_by"
                value={formData.assisted_by}
                onChange={handleTextChange}
                required
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.name_of_anesthesiologist}>
                <InputLabel id="anesthetist-label">Name Of Anaesthesiologist</InputLabel>
                <Select
                  labelId="anesthetist-label"
                  id="anesthetist-select"
                  name="name_of_anesthesiologist"
                  value={formData.name_of_anesthesiologist}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  fullWidth
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  <ListSubheader>Anaesthesia Specialists</ListSubheader>
                  {doctorGroups.AnaesthesiaSpecialists.map((doctor, index) => (
                    <MenuItem key={`anesthesia-${index}`} value={doctor}>
                      {doctor}
                    </MenuItem>
                  ))}
                </Select>
                {errors.name_of_anesthesiologist && <FormHelperText>{errors.name_of_anesthesiologist}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                label="Scrubbed Nurse"
                name="scrubbed_nurse"
                value={formData.scrubbed_nurse || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, scrubbed_nurse: val }));
                  if (errors.scrubbed_nurse) {
                    setErrors(prev => ({ ...prev, scrubbed_nurse: '' }));
                  }
                }}
                onBlur={handleBlur}
                required
                fullWidth
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  },
                }}
              >
                {Object.entries(nurseGroups).map(([group, nurses]) => [
                  <ListSubheader key={`header-${group}`} disableSticky>
                    {group}
                  </ListSubheader>,
                  ...nurses.map((nurse, index) => (
                    <MenuItem key={`${group}-${nurse}-${index}`} value={nurse}>
                      {nurse}
                    </MenuItem>
                  ))
                ]).flat()}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                label="Type of Anaesthetics"
                name="type_of_anesthesia"
                value={formData.type_of_anesthesia}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, type_of_anesthesia: val }));
                  if (errors.type_of_anesthesia) {
                    setErrors(prev => ({ ...prev, type_of_anesthesia: '' }));
                  }
                }}
                onBlur={handleBlur}
                required
                fullWidth
              >
                <MenuItem value="GA">GA</MenuItem>
                <MenuItem value="SA">SA</MenuItem>
                <MenuItem value="RA">RA</MenuItem>
                <MenuItem value="LA">LA</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Select Nurse"
                name="circulating_nurse"
                value={formData.circulating_nurse || ''}
                onChange={(e) => setFormData({ ...formData, circulating_nurse: e.target.value })}
                fullWidth
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  },
                }}
              >
                {Object.entries(nurseGroups).map(([group, nurses]) => [
                  <ListSubheader key={`header-${group}`} disableSticky>
                    {group}
                  </ListSubheader>,
                  ...nurses.map((nurse, index) => (
                    <MenuItem key={`${group}-${nurse}-${index}`} value={nurse}>
                      {nurse}
                    </MenuItem>
                  ))
                ]).flat()}
              </TextField>
            </Grid>
          </Grid>

          <br />
          <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
            Operation Notes
          </Typography>
          <textarea
            style={{ width: "97%", height: "100px", border: "1px solid black", padding: "10px" }}
            value={formData.operation_notes}
            onChange={(e) => setFormData({ ...formData, operation_notes: e.target.value })}
          />
          <Box sx={{ mt: 1 }}>
            {operationNoteSuggestions.map((text, idx) => (
              <Chip
                key={idx}
                label={text}
                onClick={() => addSuggestion("operation_notes", text)}
                sx={{ mr: 1, mb: 1, cursor: "pointer" }}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>

          <Typography variant="h5" sx={{ textAlign: "center", mt: 4, mb: 2 }}>
            Findings
          </Typography>
          <textarea
            style={{ width: "97%", height: "100px", border: "1px solid black", padding: "10px" }}
            value={formData.findings}
            onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
          />
          <Box sx={{ mt: 1 }}>
            {findingsSuggestions.map((text, idx) => (
              <Chip
                key={idx}
                label={text}
                onClick={() => addSuggestion("findings", text)}
                sx={{ mr: 1, mb: 1, cursor: "pointer" }}
                color="secondary"
                variant="outlined"
              />
            ))}
          </Box>

          <br />
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
            <DraftSaveButton
              formKey={DRAFT_KEYS.OPERATION_SHEET}
              uhidNo={uhid_no}
              formData={formData}
              onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
              variant="outlined"
              size="large"
              disabled={loading}
              isUpdate={success}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Submitting...' : (success === "success" ? 'Update' : 'Submit')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default OperationSheet;